import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  listCorretores, getCorretorDocs,
  aprovarCorretor, rejeitarCorretor, updateComissaoSplit,
} from '../../services/corretorService';

const STATUS_LABEL = {
  pendente: 'Pendente',
  aprovado: 'Aprovado',
  rejeitado: 'Rejeitado',
  suspenso: 'Suspenso',
};

const STATUS_OPTS = [
  { value: '', label: 'Todos' },
  { value: 'pendente', label: 'Pendentes' },
  { value: 'aprovado', label: 'Aprovados' },
  { value: 'rejeitado', label: 'Rejeitados' },
  { value: 'suspenso', label: 'Suspensos' },
];

const Header = styled.div`
  display: flex; flex-wrap: wrap; align-items: end; justify-content: space-between;
  gap: 16px; margin-bottom: 20px;
  h1 { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 32px; font-weight: 500; margin: 0; color: ${p => p.theme.ink}; }
  p { font-size: 13px; color: ${p => p.theme.muted}; margin: 4px 0 0; }
`;

const Filters = styled.div`
  display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 20px;
`;

const Field = styled.label`
  display: flex; flex-direction: column; gap: 6px;
  font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase; color: ${p => p.theme.muted};
  input, select {
    background: ${p => p.theme.surface};
    border: 1px solid ${p => p.theme.line}; color: ${p => p.theme.ink};
    border-radius: 8px; padding: 9px 12px; font-size: 13px; font-family: inherit;
    text-transform: none; letter-spacing: 0;
    outline: none; transition: border-color .15s;
    &:focus { border-color: ${p => p.theme.gold}; }
  }
`;

const Table = styled.div`
  background: ${p => p.theme.surface}; border: 1px solid ${p => p.theme.line};
  border-radius: 12px; overflow: hidden;
`;

const Row = styled.button`
  width: 100%; display: grid;
  grid-template-columns: 56px 1.4fr 1fr 1fr 110px 110px;
  align-items: center; gap: 16px;
  padding: 14px 18px; background: transparent; border: none;
  border-top: 1px solid ${p => p.theme.line};
  color: ${p => p.theme.ink}; text-align: left; cursor: pointer; font-family: inherit;
  transition: background .15s;
  &:first-of-type { border-top: none; }
  &:hover { background: ${p => p.theme.surfaceAlt}; }
  @media (max-width: 760px) { grid-template-columns: 1fr; gap: 4px; padding: 14px; }
`;

const Avatar = styled.div`
  width: 44px; height: 44px; border-radius: 50%;
  background: ${p => p.$src ? `url(${p.$src}) center/cover` : p.theme.surfaceAlt};
  border: 1px solid ${p => p.theme.line};
  display: flex; align-items: center; justify-content: center;
  font-size: 16px; color: ${p => p.theme.muted}; font-weight: 600;
`;

const Cell = styled.div`
  font-size: 13px; color: ${p => p.theme.ink};
  .label { font-size: 10px; letter-spacing: 1px; color: ${p => p.theme.muted}; text-transform: uppercase; display: none; }
  @media (max-width: 760px) {
    .label { display: inline; margin-right: 6px; }
  }
`;

const Badge = styled.span`
  display: inline-block; padding: 4px 10px; border-radius: 999px;
  font-size: 11px; font-weight: 600; letter-spacing: .4px; text-transform: uppercase;
  background: ${p => {
    if (p.$status === 'aprovado') return p.theme.okSoftBg;
    if (p.$status === 'pendente') return p.theme.goldSoft;
    if (p.$status === 'rejeitado') return 'rgba(154,59,59,.1)';
    return p.theme.surfaceAlt;
  }};
  color: ${p => {
    if (p.$status === 'aprovado') return p.theme.ok;
    if (p.$status === 'pendente') return p.theme.gold;
    if (p.$status === 'rejeitado') return '#9A3B3B';
    return p.theme.muted;
  }};
`;

const Empty = styled.div`
  padding: 80px 20px; text-align: center; color: ${p => p.theme.muted};
  font-size: 14px;
`;

// ===== Drawer =====
const DrawerOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 50;
  display: flex; justify-content: flex-end;
`;

const Drawer = styled.div`
  width: 480px; max-width: 100%; height: 100vh; overflow-y: auto;
  background: ${p => p.theme.surface}; padding: 28px 28px 40px;
  display: flex; flex-direction: column; gap: 18px;
`;

const DrawerHead = styled.div`
  display: flex; align-items: start; justify-content: space-between; gap: 12px;
  border-bottom: 1px solid ${p => p.theme.line}; padding-bottom: 16px;
  h2 { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 24px; font-weight: 500; margin: 0; color: ${p => p.theme.ink}; }
  small { color: ${p => p.theme.muted}; font-size: 12px; }
`;

const CloseBtn = styled.button`
  background: transparent; border: none; color: ${p => p.theme.muted};
  font-size: 22px; cursor: pointer; line-height: 1;
  &:hover { color: ${p => p.theme.ink}; }
`;

const Section = styled.section`
  display: flex; flex-direction: column; gap: 10px;
  h3 { font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase; color: ${p => p.theme.muted}; margin: 0; font-weight: 600; }
`;

const KV = styled.div`
  display: grid; grid-template-columns: 110px 1fr; gap: 8px; font-size: 13px;
  span:first-child { color: ${p => p.theme.muted}; }
  span:last-child { color: ${p => p.theme.ink}; word-break: break-word; }
`;

const DocsGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 10px;
`;

const DocCard = styled.a`
  display: block; aspect-ratio: 1; background: ${p => `url(${p.$src}) center/cover, ${p.theme.surfaceAlt}`};
  border-radius: 8px; border: 1px solid ${p => p.theme.line}; position: relative; overflow: hidden;
  text-decoration: none;
  &::after {
    content: attr(data-label);
    position: absolute; left: 0; right: 0; bottom: 0;
    padding: 6px 8px; font-size: 10px; text-transform: uppercase; letter-spacing: 1px;
    background: rgba(0,0,0,.55); color: #fff;
  }
`;

const Actions = styled.div`
  display: flex; gap: 10px; flex-wrap: wrap; margin-top: 8px;
`;

const Btn = styled.button`
  flex: 1; min-width: 130px;
  padding: 11px 16px; border-radius: 8px; font-size: 12px; font-weight: 600;
  letter-spacing: 1.2px; text-transform: uppercase; cursor: pointer; font-family: inherit;
  border: 1px solid;
  background: ${p => p.$variant === 'approve' ? p.theme.ok
    : p.$variant === 'reject' ? '#9A3B3B'
    : 'transparent'};
  color: ${p => p.$variant ? '#fff' : p.theme.ink};
  border-color: ${p => p.$variant === 'approve' ? p.theme.ok
    : p.$variant === 'reject' ? '#9A3B3B'
    : p.theme.line};
  transition: transform .15s, box-shadow .15s;
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 12px ${p => p.theme.shadow}; }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

const Msg = styled.div`
  padding: 10px 12px; border-radius: 8px; font-size: 12px;
  background: ${p => p.$ok ? p.theme.okSoftBg : 'rgba(154,59,59,.08)'};
  color: ${p => p.$ok ? p.theme.ok : '#9A3B3B'};
`;

const Textarea = styled.textarea`
  width: 100%; min-height: 90px;
  background: ${p => p.theme.bg}; color: ${p => p.theme.ink};
  border: 1px solid ${p => p.theme.line}; border-radius: 8px;
  padding: 10px 12px; font-family: inherit; font-size: 13px; resize: vertical;
  outline: none;
  &:focus { border-color: ${p => p.theme.gold}; }
`;

const Input = styled.input`
  background: ${p => p.theme.bg}; color: ${p => p.theme.ink};
  border: 1px solid ${p => p.theme.line}; border-radius: 8px;
  padding: 9px 12px; font-size: 13px; font-family: inherit;
  outline: none;
  &:focus { border-color: ${p => p.theme.gold}; }
`;

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('pt-BR'); } catch { return iso; }
}

function initials(nome) {
  if (!nome) return '?';
  const parts = nome.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '')).toUpperCase();
}

export default function CorretoresPanelV2({ theme }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null); // corretor row
  const [docs, setDocs] = useState([]);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [motivoRej, setMotivoRej] = useState('');
  const [comissao, setComissao] = useState(30);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listCorretores({ status: statusFilter || null });
      setItems(rows);
    } catch (err) {
      console.error('[corretores] listCorretores', err);
      setMsg({ ok: false, text: err.message || 'Erro ao carregar corretores' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(c =>
      (c.nome || '').toLowerCase().includes(q)
      || (c.email || '').toLowerCase().includes(q)
      || (c.creci || '').toLowerCase().includes(q)
      || (c.cidade || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const openDetail = async (corretor) => {
    setSelected(corretor);
    setMsg(null);
    setMotivoRej('');
    setComissao(corretor.comissao_split ?? 30);
    setDocs([]);
    try {
      const d = await getCorretorDocs(corretor.id);
      setDocs(d);
    } catch (err) {
      console.error('[corretores] getCorretorDocs', err);
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setDocs([]);
    setMsg(null);
  };

  const handleAprovar = async () => {
    if (!selected) return;
    if (!window.confirm(`Aprovar ${selected.nome}? Vai enviar email com link pra criar senha.`)) return;
    setBusy(true); setMsg(null);
    try {
      // salva comissao antes (caso admin alterou)
      if (Number(comissao) !== Number(selected.comissao_split)) {
        await updateComissaoSplit(selected.id, Number(comissao));
      }
      const res = await aprovarCorretor(selected.id, Number(comissao));
      if (!res.ok) { setMsg({ ok: false, text: res.error }); return; }
      setMsg({ ok: true, text: 'Corretor aprovado. Email enviado.' });
      await fetchList();
      // o id pode ter mudado (PK = auth.users.id) — fecha drawer
      setTimeout(() => closeDetail(), 1200);
    } finally {
      setBusy(false);
    }
  };

  const handleRejeitar = async () => {
    if (!selected) return;
    const motivo = motivoRej.trim();
    if (motivo.length < 5) { setMsg({ ok: false, text: 'Informe o motivo (mínimo 5 caracteres).' }); return; }
    if (!window.confirm(`Rejeitar ${selected.nome}?`)) return;
    setBusy(true); setMsg(null);
    try {
      const res = await rejeitarCorretor(selected.id, motivo);
      if (!res.ok) { setMsg({ ok: false, text: res.error }); return; }
      setMsg({ ok: true, text: 'Corretor rejeitado. Email enviado.' });
      await fetchList();
      setTimeout(() => closeDetail(), 1200);
    } finally {
      setBusy(false);
    }
  };

  const handleSalvarComissao = async () => {
    if (!selected) return;
    setBusy(true); setMsg(null);
    try {
      await updateComissaoSplit(selected.id, Number(comissao));
      setMsg({ ok: true, text: 'Comissão atualizada.' });
      await fetchList();
    } catch (err) {
      setMsg({ ok: false, text: err.message || 'Erro ao salvar comissão' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Header>
        <div>
          <h1>Corretores</h1>
          <p>{filtered.length} {filtered.length === 1 ? 'corretor' : 'corretores'} {filtered.length !== items.length && `de ${items.length}`}</p>
        </div>
      </Header>

      <Filters>
        <Field>
          Status
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            {STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field style={{ flex: 1, minWidth: 220 }}>
          Buscar
          <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Nome, email, CRECI, cidade" />
        </Field>
      </Filters>

      {loading && <Empty>Carregando...</Empty>}
      {!loading && filtered.length === 0 && (
        <Empty>Nenhum corretor {statusFilter ? `com status "${STATUS_LABEL[statusFilter]}"` : 'cadastrado'}.</Empty>
      )}

      {!loading && filtered.length > 0 && (
        <Table>
          {filtered.map(c => (
            <Row key={c.id} onClick={() => openDetail(c)}>
              <Avatar $src={c.foto_url}>{!c.foto_url && initials(c.nome)}</Avatar>
              <Cell>
                <div style={{ fontWeight: 600 }}>{c.nome}</div>
                <div style={{ fontSize: 12, color: theme?.muted || '#888' }}>{c.email}</div>
              </Cell>
              <Cell><span className="label">CRECI</span>{c.creci}{c.creci_uf && `-${c.creci_uf}`}</Cell>
              <Cell><span className="label">Cidade</span>{c.cidade || '—'}</Cell>
              <Cell><span className="label">Cadastro</span>{fmtDate(c.created_at)}</Cell>
              <Cell style={{ textAlign: 'right' }}>
                <Badge $status={c.status}>{STATUS_LABEL[c.status] || c.status}</Badge>
              </Cell>
            </Row>
          ))}
        </Table>
      )}

      {selected && (
        <DrawerOverlay onClick={(e) => { if (e.target === e.currentTarget) closeDetail(); }}>
          <Drawer>
            <DrawerHead>
              <div>
                <h2>{selected.nome}</h2>
                <small>{selected.email} · {selected.telefone || 'sem telefone'}</small>
              </div>
              <CloseBtn onClick={closeDetail} aria-label="Fechar">×</CloseBtn>
            </DrawerHead>

            <Section>
              <h3>Dados</h3>
              <KV><span>Status</span><span><Badge $status={selected.status}>{STATUS_LABEL[selected.status]}</Badge></span></KV>
              <KV><span>CRECI</span><span>{selected.creci}{selected.creci_uf && `-${selected.creci_uf}`}</span></KV>
              <KV><span>Cidade</span><span>{selected.cidade || '—'}</span></KV>
              <KV><span>Cadastrado</span><span>{fmtDate(selected.created_at)}</span></KV>
              {selected.aprovado_em && (
                <KV><span>Aprovado em</span><span>{fmtDate(selected.aprovado_em)} por {selected.aprovado_por || '—'}</span></KV>
              )}
              {selected.motivo_rejeicao && (
                <KV><span>Motivo rejeição</span><span>{selected.motivo_rejeicao}</span></KV>
              )}
              {selected.bio && (
                <KV><span>Bio</span><span>{selected.bio}</span></KV>
              )}
            </Section>

            <Section>
              <h3>Comissão do corretor</h3>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Input
                  type="number" min="0" max="100" step="0.5"
                  value={comissao}
                  onChange={e => setComissao(e.target.value)}
                  style={{ width: 100 }}
                />
                <span style={{ fontSize: 13, color: theme?.muted || '#888' }}>%</span>
                {selected.status !== 'pendente' && (
                  <Btn type="button" onClick={handleSalvarComissao} disabled={busy} style={{ flex: 0 }}>
                    Salvar
                  </Btn>
                )}
              </div>
            </Section>

            <Section>
              <h3>Documentos ({docs.length})</h3>
              {docs.length === 0 && <small style={{ fontSize: 12, color: theme?.muted || '#888' }}>Sem documentos enviados.</small>}
              {docs.length > 0 && (
                <DocsGrid>
                  {docs.map(d => (
                    <DocCard
                      key={d.id}
                      href={d.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      $src={d.url}
                      data-label={d.tipo.replace('_', ' ')}
                    />
                  ))}
                </DocsGrid>
              )}
            </Section>

            {selected.status === 'pendente' && (
              <>
                <Section>
                  <h3>Rejeitar (opcional)</h3>
                  <Textarea
                    placeholder="Motivo da rejeição (será enviado por email ao corretor)"
                    value={motivoRej}
                    onChange={e => setMotivoRej(e.target.value)}
                  />
                </Section>

                <Actions>
                  <Btn $variant="reject" type="button" onClick={handleRejeitar} disabled={busy}>
                    {busy ? 'Aguarde...' : 'Rejeitar'}
                  </Btn>
                  <Btn $variant="approve" type="button" onClick={handleAprovar} disabled={busy}>
                    {busy ? 'Aguarde...' : 'Aprovar'}
                  </Btn>
                </Actions>
              </>
            )}

            {msg && <Msg $ok={msg.ok}>{msg.text}</Msg>}
          </Drawer>
        </DrawerOverlay>
      )}
    </div>
  );
}
